package com.mps.asc_app.presentation

import com.mps.asc_app.data.auth.AuthRepository
import com.mps.asc_app.config.DebugRegistrationConfig
import com.mps.asc_app.data.config.ConfigRepository
import com.mps.asc_app.data.functions.FunctionsRepository
import com.mps.asc_app.data.places.model.AddressSuggestion
import com.mps.asc_app.data.user.ApprovalProfile
import com.mps.asc_app.data.user.UserRepository
import com.mps.asc_app.data.user.UserStatus
import com.mps.asc_app.domain.places.usecase.ValidateAddressUseCase
import com.mps.asc_app.util.PRIVACY_CONSENT_VERSION
import com.mps.asc_app.util.currentEpochMillis
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

data class MainUiState(
    val isLoggedIn: Boolean = false,
    val error: String? = null,
    val isLoading: Boolean = false,
    val isValidatingAddress: Boolean = false,
    val isAddressValid: Boolean = false,
    val addressError: String? = null,
    val addressQuery: String = "",
    val addressSuggestions: List<AddressSuggestion> = emptyList(),
    val selectedPlaceId: String? = null,
    val isCheckingApproval: Boolean = false,
    val userStatus: UserStatus? = null,
    val forceAscMembershipFormOnly: Boolean = false,
    val forcePadelMembershipFormOnly: Boolean = false,
    val membershipRequestSubmitted: Boolean = false,
    val isAdmin: Boolean = false,
    val isTrainer: Boolean = false,
    val trainerAssignments: List<String> = emptyList(),
    val isResendingVerificationEmail: Boolean = false,
    val resendEmailSuccess: Boolean = false,
    val resendEmailError: String? = null,
    val isSendingPasswordResetEmail: Boolean = false,
    val passwordResetSuccess: Boolean = false,
    val passwordResetError: String? = null,

    val needsPrivacyConsent: Boolean = false,
    val isAcceptingPrivacyConsent: Boolean = false,
    val privacyConsentError: String? = null,
)

sealed interface MainNavEvent {
    data object OpenAdminPanel : MainNavEvent
    data object GoHome : MainNavEvent
}

class MainViewModel(
    private val authRepository: AuthRepository,
    private val functionsRepository: FunctionsRepository,
    private val userRepository: UserRepository,
    private val configRepository: ConfigRepository,
    private val validateAddressUseCase: ValidateAddressUseCase,
    private val autocompleteAddress: suspend (String) -> List<AddressSuggestion>,
    private val verifyAndSaveUserProfile: suspend (
        uid: String,
        firstName: String,
        lastName: String,
        email: String,
        placeId: String
    ) -> Result<Unit>,
    private val savePhoneNumber: suspend (String, String) -> Result<Unit>,
    private val approvalStepTimeoutMillis: Long = APPROVAL_STEP_TIMEOUT_MILLIS,
) : BaseViewModel() {

    private companion object {
        // Jede Anfrage nach dem Login bekommt ein Zeitlimit, damit "Lade…" nie endlos haengt.
        const val APPROVAL_STEP_TIMEOUT_MILLIS = 10_000L
        const val APPROVAL_MAX_ATTEMPTS = 2
        const val APPROVAL_RETRY_DELAY_MILLIS = 1_000L
    }

    private class ApprovalStepTimeoutException(step: String) :
        Exception("Zeitüberschreitung beim Laden: $step")

    private sealed interface ApprovalOutcome {
        data object EmailNotVerified : ApprovalOutcome
        data class Loaded(val profile: ApprovalProfile, val needsPrivacyConsent: Boolean) : ApprovalOutcome
    }

    /**
     * Diese Datenschutzerklärung bezieht sich auf die Sichtbarkeit von Vor-/Nachnamen.
     * Wenn Namen im Buchungstool deaktiviert sind, ist keine Einwilligung erforderlich.
     */
    private suspend fun isPrivacyConsentRequired(): Boolean {
        return runCatching { configRepository.getTimeslotConfig().bookingNamesPublic }
            .getOrElse { true } // fail-safe: im Zweifel Einwilligung verlangen
    }

    private val _navEvents = MutableSharedFlow<MainNavEvent>(extraBufferCapacity = 1)
    val navEvents: SharedFlow<MainNavEvent> = _navEvents

    private val _uiState = MutableStateFlow(
        MainUiState(
            isLoggedIn = authRepository.isLoggedIn()
        )
    )

    val uiState: StateFlow<MainUiState> = _uiState

    private var currentUid: String? = null
    private var addressJob: Job? = null
    private var approvalCheckJob: Job? = null

    fun requestOpenAdminPanel() {
        _navEvents.tryEmit(MainNavEvent.OpenAdminPanel)
    }

    fun requestGoHome() {
        _navEvents.tryEmit(MainNavEvent.GoHome)
    }

    private suspend fun <T : Any> withApprovalTimeout(step: String, block: suspend () -> T): T =
        withTimeoutOrNull(approvalStepTimeoutMillis) { block() }
            ?: throw ApprovalStepTimeoutException(step)

    // Drei Anfragen: E-Mail-Status, das Nutzerprofil (ein Dokument) und die Buchungs-Konfiguration.
    private suspend fun loadApproval(uid: String): ApprovalOutcome {
        val isEmailVerified = withApprovalTimeout("E-Mail-Status") { authRepository.isEmailVerified() }
        if (!isEmailVerified) return ApprovalOutcome.EmailNotVerified

        val profile = withApprovalTimeout("Profil") { userRepository.getApprovalProfile(uid) }

        // Datenschutz-Einwilligung nur prüfen, wenn Namen im Tool sichtbar sind.
        // fail-safe: im Zweifel (auch bei Zeitüberschreitung) Einwilligung verlangen
        val consentRequired = withTimeoutOrNull(approvalStepTimeoutMillis) { isPrivacyConsentRequired() } ?: true
        val hasConsent = profile.acceptedPrivacyConsentVersion == PRIVACY_CONSENT_VERSION

        return ApprovalOutcome.Loaded(profile, needsPrivacyConsent = consentRequired && !hasConsent)
    }

    private suspend fun loadApprovalWithRetry(uid: String): Result<ApprovalOutcome> {
        var lastError: Exception? = null
        repeat(APPROVAL_MAX_ATTEMPTS) { attempt ->
            try {
                return Result.success(loadApproval(uid))
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                lastError = e
            }
            if (attempt < APPROVAL_MAX_ATTEMPTS - 1) delay(APPROVAL_RETRY_DELAY_MILLIS)
        }
        return Result.failure(lastError ?: IllegalStateException("Status konnte nicht geladen werden"))
    }

    private fun checkApproval(uid: String) {

        approvalCheckJob?.cancel()

        approvalCheckJob = scope.launch {

            _uiState.update {
                it.copy(
                    isCheckingApproval = true,
                    error = null
                )
            }

            val result = loadApprovalWithRetry(uid)

            // Ignore stale results when user changed while request was running.
            if (currentUid != uid) {
                return@launch
            }

            result.fold(
                onSuccess = { outcome ->
                    when (outcome) {
                        ApprovalOutcome.EmailNotVerified -> _uiState.update {
                            it.copy(
                                isCheckingApproval = false,
                                userStatus = UserStatus.EMAIL_NOT_VERIFIED,
                                forceAscMembershipFormOnly = false,
                                forcePadelMembershipFormOnly = false,
                                membershipRequestSubmitted = false,
                                isAdmin = false,
                                isTrainer = false,
                                trainerAssignments = emptyList(),
                                needsPrivacyConsent = false,
                                isAcceptingPrivacyConsent = false,
                                privacyConsentError = null,
                                error = null
                            )
                        }

                        is ApprovalOutcome.Loaded -> _uiState.update {
                            val profile = outcome.profile
                            it.copy(
                                isCheckingApproval = false,
                                userStatus = profile.status,
                                forceAscMembershipFormOnly = profile.forceAscMembershipForm,
                                forcePadelMembershipFormOnly = profile.forcePadelMembershipForm,
                                membershipRequestSubmitted = profile.membershipRequestSubmitted,
                                isAdmin = profile.isAdmin,
                                isTrainer = profile.isTrainer,
                                trainerAssignments = profile.trainerAssignments,
                                needsPrivacyConsent = outcome.needsPrivacyConsent,
                                privacyConsentError = null,
                                error = null
                            )
                        }
                    }
                },
                onFailure = { e ->
                    _uiState.update {
                        it.copy(
                            isCheckingApproval = false,
                            forceAscMembershipFormOnly = false,
                            forcePadelMembershipFormOnly = false,
                            membershipRequestSubmitted = false,
                            isAdmin = false,
                            isTrainer = false,
                            trainerAssignments = emptyList(),
                            needsPrivacyConsent = false,
                            error = "Dein Profil konnte nicht geladen werden. Bitte prüfe deine " +
                                "Verbindung und tippe auf „Neu versuchen“. (${e.message ?: "unbekannter Fehler"})"
                        )
                    }
                }
            )
        }
    }

    fun acceptPrivacyConsent() {
        val uid = currentUid ?: authRepository.currentUser()?.uid
        if (uid.isNullOrBlank()) {
            _uiState.update { it.copy(privacyConsentError = "Kein aktiver Benutzer gefunden") }
            return
        }

        scope.launch {
            // Wenn Namen im Buchungstool deaktiviert sind, ist keine Einwilligung nötig.
            val required = isPrivacyConsentRequired()
            if (!required) {
                _uiState.update {
                    it.copy(
                        needsPrivacyConsent = false,
                        isAcceptingPrivacyConsent = false,
                        privacyConsentError = null
                    )
                }
                checkApproval(uid)
                return@launch
            }

            _uiState.update {
                it.copy(
                    isAcceptingPrivacyConsent = true,
                    privacyConsentError = null
                )
            }

            val result = userRepository.acceptPrivacyConsent(uid, PRIVACY_CONSENT_VERSION)

            result.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            isAcceptingPrivacyConsent = false,
                            needsPrivacyConsent = false,
                            privacyConsentError = null
                        )
                    }
                    checkApproval(uid)
                },
                onFailure = { e ->
                    _uiState.update {
                        it.copy(
                            isAcceptingPrivacyConsent = false,
                            privacyConsentError = e.message ?: "Einwilligung konnte nicht gespeichert werden."
                        )
                    }
                }
            )
        }
    }

    private suspend fun awaitSignedInUid(timeoutMillis: Long = 3000): String? {

        currentUid?.let { return it }

        return withTimeoutOrNull(timeoutMillis) {
            while (currentUid == null) {
                delay(50)
            }
            currentUid
        }
    }

    init {
        scope.launch {

            authRepository.authState.collect { user ->

                val loggedIn = user != null

                _uiState.update { it.copy(isLoggedIn = loggedIn) }

                if (!loggedIn) {

                    approvalCheckJob?.cancel()

                    currentUid = null

                    _uiState.update {
                        it.copy(
                            isCheckingApproval = false,
                            userStatus = null,
                            forceAscMembershipFormOnly = false,
                            forcePadelMembershipFormOnly = false,
                            membershipRequestSubmitted = false,
                            isAdmin = false,
                            isTrainer = false,
                            trainerAssignments = emptyList(),
                            needsPrivacyConsent = false,
                            isAcceptingPrivacyConsent = false,
                            privacyConsentError = null,
                        )
                    }

                    return@collect
                }

                val uid = user.uid
                currentUid = uid

                checkApproval(uid)
            }
        }
    }

    fun login(email: String, password: String) {
        val trimmedEmail = email.trim()
        if (DebugRegistrationConfig.isManagedTestUserEmail(trimmedEmail) && !DebugRegistrationConfig.enabled) {
            _uiState.update {
                it.copy(
                    isLoggedIn = false,
                    isLoading = false,
                    error = "Dieser Testnutzer darf sich nur auf localhost anmelden."
                )
            }
            return
        }

        scope.launch {

            _uiState.update { it.copy(isLoading = true, error = null) }

            val result = authRepository.login(trimmedEmail, password)

            _uiState.update { state ->

                result.fold(

                    onSuccess = {
                        state.copy(
                            isLoading = false,
                            error = null
                        )
                    },

                    onFailure = { e ->
                        state.copy(
                            isLoggedIn = false,
                            isLoading = false,
                            error = e.message ?: "Login fehlgeschlagen"
                        )
                    }
                )
            }
        }
    }

    fun requestPasswordReset(email: String) {
        val trimmedEmail = email.trim()
        if (trimmedEmail.isBlank()) {
            _uiState.update {
                it.copy(
                    passwordResetSuccess = false,
                    passwordResetError = "Bitte E-Mail-Adresse eingeben."
                )
            }
            return
        }

        scope.launch {
            _uiState.update {
                it.copy(
                    isSendingPasswordResetEmail = true,
                    passwordResetSuccess = false,
                    passwordResetError = null
                )
            }

            val result = authRepository.sendPasswordResetEmail(trimmedEmail)

            _uiState.update { state ->
                result.fold(
                    onSuccess = {
                        state.copy(
                            isSendingPasswordResetEmail = false,
                            passwordResetSuccess = true,
                            passwordResetError = null
                        )
                    },
                    onFailure = { e ->
                        state.copy(
                            isSendingPasswordResetEmail = false,
                            passwordResetSuccess = false,
                            passwordResetError = e.message ?: "Passwort-Reset fehlgeschlagen"
                        )
                    }
                )
            }
        }
    }

    fun clearPasswordResetFeedback() {
        _uiState.update {
            it.copy(
                passwordResetSuccess = false,
                passwordResetError = null
            )
        }
    }

    fun register(
        email: String,
        password: String,
        confirmPassword: String,
        firstName: String,
        lastName: String,
        phone: String,
        placeId: String?,
        privacyAccepted: Boolean,
    ) {

        val trimmedEmail = email.trim()
        val trimmedFirstName = firstName.trim()
        val trimmedLastName = lastName.trim()
        val trimmedPhone = phone.trim()
        val trimmedPlaceId = placeId?.trim().orEmpty()

        if (
            trimmedEmail.isBlank() ||
            password.isBlank() ||
            confirmPassword.isBlank() ||
            trimmedFirstName.isBlank() ||
            trimmedLastName.isBlank() ||
            trimmedPhone.isBlank()
        ) {
            _uiState.update {
                it.copy(error = "Bitte alle Pflichtfelder ausfüllen.")
            }
            return
        }

        if (password != confirmPassword) {
            _uiState.update {
                it.copy(error = "Passwörter stimmen nicht überein.")
            }
            return
        }

        if (trimmedPlaceId.isBlank()) {
            _uiState.update {
                it.copy(addressError = "Bitte eine Adresse aus den Vorschlägen auswählen.")
            }
            return
        }

        scope.launch {

            // Datenschutz-Einwilligung nur erzwingen, wenn Namen im Tool sichtbar sind.
            val consentRequired = isPrivacyConsentRequired()
            if (consentRequired && !privacyAccepted) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = "Bitte bestaetige die Datenschutzerklaerung."
                    )
                }
                return@launch
            }

            _uiState.update {
                it.copy(
                    isLoading = true,
                    isValidatingAddress = true,
                    error = null,
                    addressError = null
                )
            }

            val validatedAddress = runCatching {
                validateAddressUseCase(trimmedPlaceId)
            }.getOrNull()

            val isValid = validatedAddress != null

            _uiState.update {
                it.copy(
                    isValidatingAddress = false,
                    isAddressValid = isValid
                )
            }

            if (!isValid) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        addressError = "Bitte eine gültige Adresse aus den Vorschlägen auswählen."
                    )
                }
                return@launch
            }

            // -------------------------
            // Firebase Auth Registrierung
            // -------------------------
            val authResult = authRepository.register(trimmedEmail, password)

            if (authResult.isFailure) {

                val exception = authResult.exceptionOrNull()

                val errorMessage = when {
                    exception?.message?.contains("auth/email-already-in-use", true) == true ->
                        "Diese E-Mail-Adresse ist bereits registriert."

                    exception?.message?.contains("auth/invalid-email", true) == true ->
                        "Die E-Mail-Adresse ist ungültig."

                    exception?.message?.contains("auth/weak-password", true) == true ->
                        "Das Passwort ist zu schwach."

                    else ->
                        exception?.message ?: "Registrierung fehlgeschlagen."
                }

                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = errorMessage
                    )
                }

                return@launch
            }

            // -------------------------
            // Verifizierungs-Mail senden
            // -------------------------
            runCatching {
                authRepository.sendEmailVerification()
            }.getOrElse {
                Result.failure(it)
            }


            // -------------------------
            // UID holen
            // -------------------------
            val uid = awaitSignedInUid()

            if (uid == null) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = "Profil konnte nicht geladen werden."
                    )
                }
                return@launch
            }

            // -------------------------
            // Profil speichern
            // -------------------------
            val profileResult = verifyAndSaveUserProfile(
                uid,
                trimmedFirstName,
                trimmedLastName,
                trimmedEmail,
                trimmedPlaceId
            )


            if (profileResult.isFailure) {

                savePhoneNumber(uid, trimmedPhone)

                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = profileResult.exceptionOrNull()?.message
                            ?: "Profil konnte nicht vollständig gespeichert werden."
                    )
                }

                return@launch
            }

            // -------------------------
            // Datenschutz-Einwilligung speichern
            // -------------------------
            if (consentRequired) {
                userRepository.acceptPrivacyConsent(uid, PRIVACY_CONSENT_VERSION)
            }

            // -------------------------
            // Telefonnummer speichern
            // -------------------------
            savePhoneNumber(uid, trimmedPhone)

            // -------------------------
            // Logout (wie im Flutter Flow)
            // -------------------------
            authRepository.logout()

            _uiState.update {
                it.copy(
                    isLoading = false,
                    error = null
                )
            }
        }
    }

    fun createDebugUserFromLogin() {
        if (!DebugRegistrationConfig.enabled) return

        val placeId = DebugRegistrationConfig.defaultPlaceId.trim()
        if (placeId.isBlank()) {
            _uiState.update {
                it.copy(error = "Debug-Testnutzer ist nicht konfiguriert: Bitte defaultPlaceId in DebugRegistrationConfig setzen.")
            }
            return
        }

        val now = currentEpochMillis()
        val email = "${DebugRegistrationConfig.emailPrefix}+$now@${DebugRegistrationConfig.emailDomain}"

        register(
            email = email,
            password = DebugRegistrationConfig.defaultPassword,
            confirmPassword = DebugRegistrationConfig.defaultPassword,
            firstName = DebugRegistrationConfig.firstName,
            lastName = DebugRegistrationConfig.lastName,
            phone = DebugRegistrationConfig.phone,
            placeId = placeId,
            privacyAccepted = true,
        )
    }

    // Die Test-Konten werden nicht mehr vom Login-Screen aus angelegt: die zugehoerigen
    // Cloud Functions sind nur noch fuer Admins freigegeben (Admin-Bereich -> "Test-Konten
    // zuruecksetzen"). Hier wird nur noch mit dem bestehenden Test-Konto eingeloggt.
    fun loginWithTestUserManuallyChecked() {
        if (!DebugRegistrationConfig.enabled) {
            _uiState.update { it.copy(error = "Debug mode ist nicht aktiviert.") }
            return
        }
        login(
            email = DebugRegistrationConfig.testUserCheckedNoMemberEmail,
            password = DebugRegistrationConfig.testUserCheckedNoMemberPassword,
        )
    }

    fun loginWithTestUserHasMemberNumberNoSport() {
        if (!DebugRegistrationConfig.enabled) {
            _uiState.update { it.copy(error = "Debug mode ist nicht aktiviert.") }
            return
        }
        login(
            email = DebugRegistrationConfig.testUserHasMemberNumberNoSportEmail,
            password = DebugRegistrationConfig.testUserHasMemberNumberNoSportPassword,
        )
    }

    fun logout() {

        scope.launch {

            _uiState.update { it.copy(isLoading = true, error = null) }

            val result = authRepository.logout()

            _uiState.update { state ->

                result.fold(

                    onSuccess = {

                        currentUid = null

                        state.copy(
                            isLoggedIn = false,
                            isLoading = false,
                            error = null,
                            isCheckingApproval = false,
                            userStatus = null,
                            membershipRequestSubmitted = false,
                            isAdmin = false,
                            isTrainer = false,
                            trainerAssignments = emptyList(),
                        )
                    },

                    onFailure = { e ->
                        state.copy(
                            isLoading = false,
                            error = e.message ?: "Logout fehlgeschlagen"
                        )
                    }
                )
            }
        }
    }

    fun onAddressQueryChanged(value: String) {

        val query = value.trim()

        _uiState.update {
            it.copy(
                addressQuery = value,
                selectedPlaceId = null,
                isAddressValid = false,
                addressError = null
            )
        }

        addressJob?.cancel()

        if (query.length < 3) {
            _uiState.update { it.copy(addressSuggestions = emptyList()) }
            return
        }

        addressJob = scope.launch {

            delay(300)

            val suggestions = runCatching {
                autocompleteAddress(query)
            }.getOrElse { emptyList() }

            _uiState.update {
                it.copy(addressSuggestions = suggestions)
            }
        }
    }

    fun onAddressSuggestionSelected(s: AddressSuggestion) {

        _uiState.update {
            it.copy(
                addressQuery = s.fullText,
                selectedPlaceId = s.placeId,
                addressSuggestions = emptyList(),
                isAddressValid = true,
                addressError = null
            )
        }
    }

    fun recheckApproval() {

        val uid = currentUid ?: authRepository.currentUser()?.uid

        if (uid == null) {
            _uiState.update {
                it.copy(error = "Kein aktiver Benutzer gefunden")
            }
            return
        }

        checkApproval(uid)
    }

    fun resendVerificationEmail() {
        val currentUser = authRepository.currentUser()
        if (currentUser == null) {
            _uiState.update { it.copy(resendEmailError = "Kein aktiver Benutzer gefunden.") }
            return
        }

        scope.launch {
            _uiState.update {
                it.copy(
                    isResendingVerificationEmail = true,
                    resendEmailSuccess = false,
                    resendEmailError = null
                )
            }

            val result = authRepository.sendEmailVerification()

            _uiState.update { state ->
                result.fold(
                    onSuccess = {
                        state.copy(
                            isResendingVerificationEmail = false,
                            resendEmailSuccess = true,
                            resendEmailError = null
                        )
                    },
                    onFailure = { e ->
                        state.copy(
                            isResendingVerificationEmail = false,
                            resendEmailSuccess = false,
                            resendEmailError = e.message ?: "E-Mail konnte nicht gesendet werden."
                        )
                    }
                )
            }
        }
    }
}